import { useLingui } from "@lingui/react";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useCallback, useRef } from "react";
import { STEPS, useStepNavigation } from "@/hooks/useStepNavigation";

const BOOKING_FLOW_STEPS = ["results", "extras", "details", "payment"] as const;
type BookingFlowStep = (typeof BOOKING_FLOW_STEPS)[number];

const STEP_SIZE = 30;

interface StepIndicatorProps {
  stepKey: BookingFlowStep;
  stepNumber: number;
  isActive: boolean;
  isCompleted: boolean;
  isClickable: boolean;
  onClick: () => void;
  activeRef?: React.Ref<HTMLDivElement>;
}

function StepIndicator({
  stepKey,
  stepNumber,
  isActive,
  isCompleted,
  isClickable,
  onClick,
  activeRef,
}: StepIndicatorProps) {
  const { _ } = useLingui();
  const stepConfig = STEPS[stepKey];

  return (
    <Box
      ref={activeRef}
      sx={{
        width: STEP_SIZE,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        cursor: isClickable ? "pointer" : "default",
      }}
      onClick={isClickable ? onClick : undefined}
    >
      {/* Label — absolutely positioned so it doesn't affect the step's flex width */}
      <Typography
        sx={{
          position: "absolute",
          bottom: "100%",
          mb: 0.5,
          fontWeight: isActive ? 600 : 300,
          color: isActive || isCompleted ? "text.primary" : "text.secondary",
          whiteSpace: "nowrap",
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: { xs: "14px", md: "16px" },
        }}
      >
        {_(stepConfig.title)}
      </Typography>

      {/* Circle */}
      <Box
        sx={(theme) => ({
          width: STEP_SIZE,
          height: STEP_SIZE,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
          fontSize: "0.9rem",
          fontWeight: "normal",
          zIndex: 1,
          transition: "background-color 0.5s ease",
          bgcolor:
            isCompleted || isActive ? "primary.main" : { md: "background.paper", xs: "grey.100" },
          color: isCompleted || isActive ? "background.paper" : "text.primary",

          ...(isActive && {
            outline: `4px solid ${theme.palette.primary.light}`,
            outlineOffset: 0,
          }),
        })}
      >
        {isCompleted ? <CheckIcon sx={{ width: 16, height: 16 }} /> : stepNumber}
      </Box>
    </Box>
  );
}

function Connector({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 16,
        height: 2,
        transition: "background-color 0.5s ease",
        bgcolor: active ? "primary.main" : "divider",
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Booking Progress Indicator */
/* ------------------------------------------------------------------ */

export default function BookingProgressIndicator() {
  const { currentStep, isStepAvailable, goToStep } = useStepNavigation();

  const currentStepName = currentStep === "payment-stripe" ? "payment" : currentStep;

  const currentStepIndex = Math.max(
    BOOKING_FLOW_STEPS.indexOf(currentStepName as BookingFlowStep),
    0,
  );

  // Scroll the indicator to the active step if it's not within the visible range
  const lastScrolledIndex = useRef(-1);
  const activeStepRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && lastScrolledIndex.current !== currentStepIndex) {
        lastScrolledIndex.current = currentStepIndex;
        const { left, right } = node.getBoundingClientRect();
        if (left < 0 || right > window.innerWidth) {
          node.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
      }
    },
    [currentStepIndex],
  );

  const isStepCompleted = (index: number) => index < currentStepIndex;

  const isStepActive = (index: number) => index === currentStepIndex;

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pt: 4,
        pb: 1.5,
        pl: 2,
        pr: { xs: 4, md: 2 },
        minWidth: { xs: "450px", md: "500px" },
        borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
      })}
    >
      {/* Search icon */}
      <IconButton
        sx={{
          width: STEP_SIZE + 4,
          height: STEP_SIZE + 4,
          bgcolor: { md: "background.paper", xs: "grey.100" },
          flexShrink: 0,
          "&:hover": { bgcolor: "background.paper" },
        }}
        onClick={() => goToStep("dates")}
      >
        <SearchIcon sx={{ color: "#666666", fontSize: 18 }} />
      </IconButton>

      <Connector active={false} />

      {/* Steps */}
      {BOOKING_FLOW_STEPS.map((stepKey, index) => (
        <React.Fragment key={stepKey}>
          <StepIndicator
            stepKey={stepKey}
            stepNumber={index + 1}
            isActive={isStepActive(index)}
            isCompleted={isStepCompleted(index)}
            isClickable={isStepAvailable(stepKey) && !isStepActive(index)}
            onClick={() => goToStep(stepKey)}
            activeRef={isStepActive(index) ? activeStepRef : undefined}
          />
          {index < BOOKING_FLOW_STEPS.length - 1 && <Connector active={currentStepIndex > index} />}
        </React.Fragment>
      ))}
    </Box>
  );
}
